import {Variant} from '@/types';
import {create} from 'zustand';
import {SearchParams} from "meilisearch";

/*
* This store is very important so that the variant grid does not jump up and down when new data is fetched.
* The pageKey ensures variants are only used when they match the current URL (prevents hydration errors).
* */


interface VariantStore {
    variants: Variant[];
    pageKey: string | null;  // Tracks which page/filter combination the variants belong to
    setVariants: (variants: Variant[], pageKey: string) => void;
    appendVariants: (variants: Variant[]) => void;
    clearVariants: () => void;
}


interface FilterStore {
    filter: SearchParams | null;
    pageKey: string | null;  // Tracks which page/filter combination the filter belongs to
    setFilter: (filter: SearchParams, pageKey: string) => void;
}

export const useVariantStore = create<VariantStore>((set) => ({
    variants: [],
    pageKey: null,
    setVariants: (variants, pageKey) => set({variants, pageKey}),
    // Defense-in-depth dedup against the virtualizer-effect race that could
    // fire two parallel pagination requests with the same offset. Synchronous
    // isFetchingRef in the consumer is the primary guard; this Set check
    // catches edge cases where two responses still both arrive.
    appendVariants: (newVariants) =>
        set((state) => {
            if (newVariants.length === 0) return state;
            const existing = new Set(state.variants.map(v => v.variant_id));
            const filtered = newVariants.filter(v => v.variant_id && !existing.has(v.variant_id));
            if (filtered.length === 0) return state;
            return {variants: [...state.variants, ...filtered]};
        }),
    clearVariants: () => set({variants: [], pageKey: null})
}));


export const useFilterStore = create<FilterStore>((set) => ({
    filter: null,
    pageKey: null,
    setFilter: (filter, pageKey) => set({filter, pageKey}),
}));
